import React from 'react';
import { Translation } from '../translations';

interface HeroSectionProps {
  t: Translation;
}

const HeroSection: React.FC<HeroSectionProps> = ({ t }) => {
  const getStatValue = (stat: string) => stat.split(' ')[0];
  const getStatLabel = (stat: string) => stat.split(' ').slice(1).join(' ');

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin-slow 20s linear infinite;
          }
        `}
      </style>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50 opacity-50"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-200 rounded-full blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-up">
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-6">
              <span className="animate-pulse mr-2">🚀</span>
              Next-Gen Communication Platform
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-primary-700 to-primary-600 bg-clip-text text-transparent leading-[1.2] mb-6 break-words">
              {t.hero.title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700 font-medium mb-6 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <p className="text-base sm:text-lg text-gray-600 mb-10 leading-relaxed break-words">
              {t.hero.description}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <a 
                href="/download" 
                className="group relative px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl text-center font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <span className="relative z-10">{t.hero.cta.primary}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-primary-800 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              {/* Watch Demo button temporarily hidden
              <a 
                href="/login" 
                className="px-8 py-4 bg-white/80 backdrop-blur-sm border-2 border-gray-200 text-gray-700 rounded-xl text-center font-semibold hover:bg-gray-50 hover:border-primary-300 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                {t.hero.cta.secondary}
              </a>
              */}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="group">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">{getStatValue(t.hero.stats.users)}</div>
                <div className="text-sm text-gray-600 font-medium mt-1">{getStatLabel(t.hero.stats.users)}</div>
              </div>
              <div className="group">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">{getStatValue(t.hero.stats.languages)}</div>
                <div className="text-sm text-gray-600 font-medium mt-1">{getStatLabel(t.hero.stats.languages)}</div>
              </div>
              <div className="group">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">{getStatValue(t.hero.stats.countries)}</div>
                <div className="text-sm text-gray-600 font-medium mt-1">{getStatLabel(t.hero.stats.countries)}</div>
              </div>
            </div>
          </div>
          
          {/* Right Image/Illustration */}
          <div className="relative lg:block hidden">
            <div className="relative animate-fade-up" style={{animationDelay: '0.2s'}}>
              {/* Main Card */}
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="aspect-video bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center relative overflow-hidden">
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      {/* Glowing effect behind globe */}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-green-400 to-blue-400 rounded-full blur-xl opacity-50 animate-pulse"></div>
                      
                      {/* Globe container with professional animations */}
                      <div className="relative w-full h-full flex items-center justify-center animate-spin-slow">
                        {/* Outer ring effect */}
                        <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-pulse"></div>
                        <div className="absolute inset-2 rounded-full border border-white/10 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                        
                        {/* Globe emoji with professional styling */}
                        <div className="relative text-7xl filter drop-shadow-2xl transform hover:scale-110 transition-transform duration-300">
                          <span className="block" style={{
                            fontSize: '5rem',
                            textShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(34, 211, 238, 0.3)',
                            filter: 'brightness(1.2) contrast(1.1)'
                          }}>
                            🌍
                          </span>
                        </div>
                        
                        {/* Orbital dots */}
                        <div className="absolute inset-0 animate-spin" style={{animationDuration: '10s'}}>
                          <div className="absolute top-0 left-1/2 w-2 h-2 bg-white rounded-full -translate-x-1/2 -translate-y-1"></div>
                          <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-cyan-300 rounded-full -translate-x-1/2 translate-y-1"></div>
                        </div>
                      </div>
                      
                      {/* Connection lines animation */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                        <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-white/30 to-transparent animate-pulse" style={{animationDelay: '1s'}}></div>
                      </div>
                    </div>
                    <p className="text-white text-xl font-semibold animate-fade-up">Connect Globally</p>
                    <p className="text-white/95 text-base sm:text-lg md:text-xl font-semibold mt-3 px-3 break-words bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent animate-pulse"
                       style={{
                         animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, shimmer 3s linear infinite',
                         backgroundSize: '200% auto'
                       }}>
                      Real-time Auto-Translation Video Conference
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce-slow">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">💬</span>
                  <div>
                    <p className="text-xs font-semibold text-gray-900">Live Chat</p>
                    <p className="text-xs text-gray-500">30+ Languages</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce-slow" style={{animationDelay: '1.5s'}}>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🎥</span>
                  <div>
                    <p className="text-xs font-semibold text-gray-900">Video Call</p>
                    <p className="text-xs text-gray-500">Crystal Clear</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default HeroSection;